// #387: capture + persist a referral / reseller code the buyer arrives with, so
// it survives from the `?ref=CODE` link (or a manually typed code) all the way to
// a checkout that may happen days later. Persistence is localStorage (single
// device to start). This is display/convenience only: billing RE-VALIDATES and
// applies the code server-side at checkout, so a tampered stored value never
// grants a discount on its own, it just fails validation.

const STORAGE_KEY = "digadop.referralCode";
const MAX_LEN = 64;

// A code is opaque, but bound its shape so a junk / oversized `?ref=` can never be
// stored: the alnum + dot/dash/underscore shape codes are minted in, capped length.
export function sanitizeReferralCode(raw) {
  if (typeof raw !== "string") return null;
  const code = raw.trim();
  if (!code || code.length > MAX_LEN) return null;
  if (!/^[A-Za-z0-9._-]+$/.test(code)) return null;
  return code;
}

// Persist a code. Returns the sanitized code, or null if it was malformed. Never
// throws: storage can be unavailable (private mode / quota), in which case the
// code simply is not persisted for later.
export function storeReferralCode(raw) {
  const code = sanitizeReferralCode(raw);
  if (!code) return null;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    /* storage disabled; the code just will not survive this navigation */
  }
  return code;
}

// The persisted code, or null. Re-sanitized on read so a hand-edited storage value
// cannot smuggle a bad shape downstream.
export function getStoredReferralCode() {
  try {
    if (typeof localStorage !== "undefined") return sanitizeReferralCode(localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    /* ignore */
  }
  return null;
}

export function clearStoredReferralCode() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

// Read `?ref=CODE` from a query string (defaults to the live location). If present
// and well-formed, persist and return it. Idempotent and safe to call on every
// app load. An absent / malformed `?ref=` does NOT clobber an already-stored code,
// so a buyer who followed a partner link earlier keeps it. Falls back to the
// stored code so the caller always gets the effective code (or null).
export function captureReferralFromUrl(search) {
  try {
    const qs =
      typeof search === "string"
        ? search
        : typeof window !== "undefined"
        ? window.location.search
        : "";
    const params = new URLSearchParams(qs || "");
    const code = sanitizeReferralCode(params.get("ref"));
    if (code) return storeReferralCode(code);
  } catch (e) {
    // URLSearchParams / storage can throw in exotic environments; never break load.
  }
  return getStoredReferralCode();
}
